import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Container as ContainerBootstrap, Form } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { messages } from "../messages";
import ContentWrapper from "../../../components/content-wrapper";
import { FullWidthLoader } from "../../../components/full-width-loader";
import { withPropertyData } from "../../../components/property-admin-data-provider";
import { SectionHeading } from "../../../components/section-heading";
import { useProperty } from "../../../components/property-provider";
import { usePropertyLocales } from "../../../components/property-locale-provider";
import { useApiClient, IHttpClient } from "../../../lib/http";
import { withAuthorizationVerifier } from "../../../components/authorization-verifier";

import { useDisplayApi } from "../hooks/useDisplayApi";
import { DisplayFormInput, DisplayFormType } from "../types";
import { defaultDisplayValues, refinedDisplaySchema } from "../utils/validation";
import { zodResolver } from "@hookform/resolvers/zod";
import { entityToFormValues, formValuesToDto } from "../mappers";
import { StyledCard } from "../styles";
import { PropertiesPanel } from "../renders/PropertiesPanel";
import { ContentPanel } from "../renders/ContentPanel";
import type { DisplayImage, ContentType } from "../types";
import { errorMessageBox } from "../styles";

enum CRUD_MODE {
  ADD = "ADD",
  EDIT = "EDIT",
  DUPLICATE = "DUPLICATE",
}

type TranslationRow = {
  locale?: string;
  name?: string;
  description?: string;
  id?: string;
  label?: string;
  content?: string;
};

function normalizeTranslations(list: Array<TranslationRow> | undefined, locales: string[]) {
  const byLocale: Record<string, TranslationRow> = {};
  (list || []).forEach(translation => {
    const loc = String(translation?.locale || "").trim();
    if (!loc) return;
    if (!byLocale[loc]) byLocale[loc] = translation;
  });

  return locales.map(locale => {
    const translation = byLocale[locale];
    return {
      locale,
      name: (translation?.name ?? translation?.label ?? "").toString(),
      description: (translation?.description ?? "").toString(),
      id: translation?.id,
    };
  });
}

function _DisplayForm() {
  const { id } = useParams<{ id?: string }>();
  const location = useLocation();
  const isCopy = location.pathname.includes("/display-copy/");
  const property = useProperty();
  const allowedLocales = usePropertyLocales();
  const history = useHistory();
  const httpClient: IHttpClient = useApiClient();

  const [mode, setMode] = useState<CRUD_MODE>();
  const [loading, setLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [imageFiles, setImageFiles] = useState<DisplayImage[]>([]);
  const [showError, setShowError] = useState(false);

  const { getOne, create, update } = useDisplayApi(httpClient, property.ticker);

  const methods = useForm<DisplayFormInput, unknown, DisplayFormType>({
    resolver: zodResolver(refinedDisplaySchema),
    defaultValues: defaultDisplayValues,
    mode: "onChange",
  });

  const watchEntityFilter = methods.watch("entityFilter");

  useEffect(() => {
    if (watchEntityFilter === "PROPERTY") {
      methods.setValue("tickerFilter", "");
    }
  }, [watchEntityFilter, methods]);

  const actionMessage = useMemo(() => {
    switch (mode) {
      case CRUD_MODE.EDIT:
        return { id: "display.edit", defaultMessage: "Editar Display" };
      case CRUD_MODE.DUPLICATE:
        return { id: "display.duplicate", defaultMessage: "Duplicar Display" };
      default:
        return { id: "display.create", defaultMessage: "Crear Display" };
    }
  }, [mode]);

  const handleOnBack = useCallback(() => {
    history.push(`/${property.ticker}/display`);
  }, [history, property.ticker]);

  const handlerAction = useCallback(
    async (formValues: DisplayFormType) => {
      setShowError(false);
      try {
        const normalized = normalizeTranslations(formValues.translationsList, allowedLocales);

        const payload = formValuesToDto({
          ...formValues,
          translationsList: normalized,
          imageManager: imageFiles,
        });

        const displayId = formValues.id;
        if (mode === CRUD_MODE.EDIT && displayId) {
          await update(displayId, payload);
        } else {
          await create(payload);
        }
        handleOnBack();
      } catch (e) {
        console.error("Error guardando display:", e);
        setShowError(true);
      }
    },
    [mode, update, create, handleOnBack, imageFiles, allowedLocales]
  );

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsInitialized(false);

      if (!id) {
        setMode(CRUD_MODE.ADD);

        const emptyTranslations = allowedLocales.map(locale => ({
          locale,
          name: "",
          description: "",
        }));

        methods.reset({
          ...defaultDisplayValues,
          propertyTicker: property.ticker,
          translationsList: emptyTranslations,
          contentType: "HTML" as ContentType,
        });
        setImageFiles([]);

        setIsInitialized(true);
        return;
      }

      setMode(isCopy ? CRUD_MODE.DUPLICATE : CRUD_MODE.EDIT);
      setLoading(true);
      try {
        const displayData = await getOne(id);
        if (cancelled) return;

        const values = entityToFormValues(displayData);
        if (isCopy) {
          values.id = "";
          values.ticker = "";
        }

        setImageFiles(values.imageManager ?? []);

        values.translationsList = normalizeTranslations(
          values.translationsList as any,
          allowedLocales
        );

        methods.reset(values);
        setIsInitialized(true);
      } catch (e) {
        console.error("Error loading display:", e);
        if (!cancelled) handleOnBack();
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load().catch(err => {
      console.error("Error in display form load:", err);
    });

    return () => {
      cancelled = true;
    };
  }, [id, isCopy, getOne, methods, handleOnBack, allowedLocales, property.ticker]);

  if (loading || !isInitialized) return <FullWidthLoader />;

  return (
    <ContentWrapper>
      <FormProvider {...methods}>
        <SectionHeading>
          <FormattedMessage {...actionMessage} />
        </SectionHeading>
        <ContainerBootstrap fluid>
          <StyledCard>
            <Card.Body>
              <Form onSubmit={methods.handleSubmit(handlerAction)}>
                <PropertiesPanel display={null} methods={methods} mode={mode} />
                <ContentPanel
                  methods={methods}
                  setImageFiles={setImageFiles}
                  imageFiles={imageFiles}
                />

                <div className="d-flex gap-2 mt-4">
                  <Button type="submit" disabled={methods.formState.isSubmitting}>
                    {methods.formState.isSubmitting ? (
                      <FormattedMessage {...messages.saving} />
                    ) : (
                      <FormattedMessage {...messages.save} />
                    )}
                  </Button>

                  <Button variant="light" onClick={handleOnBack}>
                    <FormattedMessage {...messages.goBack} />
                  </Button>
                </div>
                {showError && (
                  <div style={errorMessageBox}>
                    <strong>
                      <FormattedMessage {...messages.saveError} />
                    </strong>
                  </div>
                )}
              </Form>
            </Card.Body>
          </StyledCard>
        </ContainerBootstrap>
      </FormProvider>
    </ContentWrapper>
  );
}

export const DisplayForm = withAuthorizationVerifier(withPropertyData(_DisplayForm));
